<template>
  <transition
    enter-active-class="transition-opacity duration-300"
    leave-active-class="transition-opacity duration-200"
    enter-class="opacity-0"
    leave-to-class="opacity-0"
  >
    <div v-if="visible" class="recaptcha-overlay">
      <div class="recaptcha-modal">
        <!-- Header section -->
        <div class="recaptcha-header">
          <h2 class="recaptcha-title">
            {{ lang.reCAPTCHADialog.title || "Confirm you're not a robot" }}
          </h2>
        </div>

        <!-- Content section -->
        <div class="recaptcha-content">
          <div
            :id="containerId"
            class="recaptcha-container"
          ></div>
        </div>

        <!-- Footer section -->
        <div class="recaptcha-footer">
          <button
            class="recaptcha-cancel-btn"
            @click="handleCancel"
          >
            {{ lang.reCAPTCHADialog.cancelButton || 'Cancel' }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { initializeCheckboxRecaptcha, resetToScoreMode } from '@/utilities/recaptcha-enterprise.js';
import { mapState } from 'vuex';
export default {
  name: 'RecaptchaDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    action: {
      type: String,
      default: '',
    },
  },
  emits: ['resolve', 'reject', 'update:visible'],
  data() {
    return {
      containerId: 'recaptcha-checkbox-modal',
      challengeObserver: null,
      adjustRafId: null,
    };
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.initializeRecaptcha();
          this.startChallengeObserver();
          // Prevent body scroll when dialog is open
          document.body.style.overflow = 'hidden';
        } else {
          this.cleanup();
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.cleanup();
  },
  computed: {
    ...mapState('auth', [
      'lang',
    ]),
  },
  methods: {
    async initializeRecaptcha() {
      try {
        await this.$nextTick(); // Ensure DOM is updated
        this.scaleWidgetForMobile();

        const result = await initializeCheckboxRecaptcha(this.containerId, this.action);
        console.log('Checkbox reCAPTCHA result:', result);
        
        // Close the dialog before emitting resolve
        this.$emit('update:visible', false);
        this.$emit('resolve', result);
      } catch (error) {
        console.error('Checkbox reCAPTCHA error:', error);
        // Close the dialog before emitting reject
        this.$emit('update:visible', false);
        this.$emit('reject', error);
      }
    },
    
    handleCancel() {
      this.$emit('update:visible', false);
      this.$emit('reject', new Error('User cancelled reCAPTCHA verification'));
    },
    
    /**
     * Scale the reCAPTCHA checkbox widget container to fit narrow
     * mobile viewports. The widget is a fixed 304x78 iframe that
     * cannot be resized, so we use CSS transform to scale it down.
     */
    scaleWidgetForMobile() {
      if (window.innerWidth > 480) return;

      const container = document.getElementById(this.containerId);
      if (!container) return;

      const contentArea = container.parentElement;
      if (!contentArea) return;

      const availableWidth = contentArea.clientWidth;
      const widgetWidth = 304; // Google reCAPTCHA normal size (fixed)

      if (availableWidth < widgetWidth) {
        const scale = availableWidth / widgetWidth;
        container.style.transform = `scale(${scale})`;
        container.style.transformOrigin = 'top left';
        container.style.height = `${Math.ceil(78 * scale)}px`;
      }
    },

    /**
     * Watch for Google's reCAPTCHA challenge popup (image grid) and
     * scale it down on mobile to prevent viewport overflow.
     */
    startChallengeObserver() {
      if (this.challengeObserver) return;

      this.challengeObserver = new MutationObserver(() => {
        if (this.adjustRafId) return;
        this.adjustRafId = requestAnimationFrame(() => {
          this.adjustChallengePopup();
          this.adjustRafId = null;
        });
      });

      this.challengeObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    },

    adjustChallengePopup() {
      const viewportWidth = window.innerWidth;
      if (viewportWidth > 500) return;

      // Google injects the challenge popup as a separate iframe at the body level
      const challengeIframes = document.querySelectorAll(
        'iframe[src*="google.com/recaptcha"][src*="bframe"]'
      );

      challengeIframes.forEach(iframe => {
        const container = iframe.parentElement;
        if (!container) return;

        const containerWidth = container.offsetWidth;
        if (containerWidth <= 0 || containerWidth <= viewportWidth) return;

        const padding = 16;
        const scale = (viewportWidth - padding) / containerWidth;
        const leftOffset = (viewportWidth - containerWidth * scale) / 2;

        container.style.setProperty('transform', `scale(${scale})`, 'important');
        container.style.setProperty('transform-origin', 'top left', 'important');
        container.style.setProperty('left', `${leftOffset}px`, 'important');
        container.dataset.recaptchaMobileAdjusted = 'true'; // kept for cleanup only
      });
    },

    stopChallengeObserver() {
      if (this.adjustRafId) {
        cancelAnimationFrame(this.adjustRafId);
        this.adjustRafId = null;
      }
      if (this.challengeObserver) {
        this.challengeObserver.disconnect();
        this.challengeObserver = null;
      }
      // Clean up adjusted markers and styles
      document.querySelectorAll('[data-recaptcha-mobile-adjusted]').forEach(el => {
        el.style.removeProperty('transform');
        el.style.removeProperty('transform-origin');
        el.style.removeProperty('left');
        delete el.dataset.recaptchaMobileAdjusted;
      });
    },

    cleanup() {
      this.stopChallengeObserver();
      resetToScoreMode();
      document.body.style.overflow = 'auto';
    },
  },
};
</script>

<style scoped>
.recaptcha-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.recaptcha-modal {
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  padding: 24px;
  display: flex;
  flex-direction: column;
  /* Modal width = reCAPTCHA width (304px) + padding (24px * 2) = 352px */
  width: 352px;
}

.recaptcha-header {
  margin-bottom: 20px;
}

.recaptcha-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #343a40;
  text-align: left;
  line-height: 1.4;
}

.recaptcha-content {
  /* Remove extra padding since modal already has padding */
  padding: 0;
  margin-bottom: 20px;
}

.recaptcha-container {
  /* reCAPTCHA normal size: 304px x 78px */
  width: 304px;
  height: 78px;
  margin: 0 auto;
}

.recaptcha-footer {
  display: flex;
  justify-content: flex-end;
}

.recaptcha-cancel-btn {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  background-color: #f1f3f5;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.recaptcha-cancel-btn:hover {
  background-color: #e9ecef;
  border-color: #ced4da;
}

.recaptcha-cancel-btn:active {
  background-color: #dee2e6;
  border-color: #c1c9d0;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .recaptcha-modal {
    width: calc(100vw - 24px);
    max-width: 352px;
    margin: 0 12px;
    padding: 20px 12px;
  }
}
</style>
